import { brand } from 'config';
import bopsMessages from './bopsMessages';
import formatTime from './formatTime';
import injectMessage from './injectMessage';

// Helper to determine delivery day when in-store pickup is available, if the experiment flag is true
const getDeliveryDayInStoreAvailable = (
  inStorePickupDay,
  curbsideAvailableDay,
  displayDeliveryDay,
  isCurbsideAvailable,
) => {
  if (inStorePickupDay !== 'today' && inStorePickupDay !== 'tomorrow') {
    if (
      isCurbsideAvailable
      && (curbsideAvailableDay === 'today' || curbsideAvailableDay === 'tomorrow')
    ) {
      return curbsideAvailableDay;
    }
    return displayDeliveryDay;
  }
  return inStorePickupDay;
};

// Helper to determine delivery day when in-store pickup is unavailable, if the experiment flag is true
const getDeliveryDayInStoreUnavailable = (
  curbsideAvailableDay,
  displayDeliveryDay,
) => {
  if (curbsideAvailableDay === 'tomorrow' || curbsideAvailableDay === 'today') {
    return curbsideAvailableDay;
  }
  return displayDeliveryDay;
};

// Generates a message for when in-store pickup is available, if the experiment flag is true
const getInStorePickupMessage = (
  inStoreAvailableDay,
  curbsideAvailableDay,
  isCurbsideAvailable,
  pickupCutOffTimeFormatted,
) => {
  if (inStoreAvailableDay === 'today') {
    return injectMessage(bopsMessages.inStoreTodayCurbside, {
      pickupCutOffTimeFormatted,
    });
  }
  if (
    isCurbsideAvailable
    && inStoreAvailableDay !== 'tomorrow'
    && (curbsideAvailableDay === 'tomorrow' || curbsideAvailableDay === 'today')
  ) {
    if (curbsideAvailableDay === 'tomorrow') {
      return injectMessage(bopsMessages.inStoreUnavailableCurbsideTomorrow);
    }
    return injectMessage(bopsMessages.inStoreUnavailableCurbsideToday, {
      pickupCutOffTimeFormatted,
    });
  }
  return '';
};

// Generates a message for when in-store pickup is unavailable, if the experiment flag is true
const getCurbsidePickupMessage = (
  curbsideAvailableDay,
  pickupCutOffTimeFormatted,
) => {
  if (curbsideAvailableDay === 'today') {
    return injectMessage(bopsMessages.inStoreUnavailableCurbsideToday, {
      pickupCutOffTimeFormatted,
    });
  }
  return injectMessage(bopsMessages.inStoreUnavailableCurbsideTomorrow);
};

// Generate the pickup message and delivery day for the experiment case
const generateExperimentPickupMessage = (
  inStoreAvailableDay,
  curbsideAvailableDay,
  pickupCutOffTimeFormatted,
  displayDeliveryDay,
  pickupOptions,
) => {
  let pickupMsg = '';
  let pickupDeliveryDay = '';
  const isInStoreAvailable = pickupOptions?.some((option) => option?.toLowerCase() === 'in store');
  const isCurbsideAvailable = pickupOptions?.some((option) => option?.toLowerCase() === 'curbside');

  pickupMsg = isInStoreAvailable
    ? getInStorePickupMessage(
      inStoreAvailableDay,
      curbsideAvailableDay,
      isCurbsideAvailable,
      pickupCutOffTimeFormatted,
    )
    : getCurbsidePickupMessage(curbsideAvailableDay, pickupCutOffTimeFormatted);

  pickupDeliveryDay = isInStoreAvailable
    ? getDeliveryDayInStoreAvailable(
      inStoreAvailableDay,
      curbsideAvailableDay,
      displayDeliveryDay,
      isCurbsideAvailable,
    )
    : getDeliveryDayInStoreUnavailable(
      curbsideAvailableDay,
      displayDeliveryDay,
    );

  return { pickupMsg, pickupDeliveryDay };
};

// Generate the pickup label based on pickup options
const generatePickupLabel = (
  pickupOptions,
  curbsideAvailableDay,
  inStoreAvailableDay,
  pickupCutOffTimeFormatted,
  deliveryDayLowerCase,
  otherDate,
) => {
  let pickupLabel = '';

  if (pickupOptions && pickupOptions.length > 1 && otherDate) { // both available, inStore and curbside on DIFFERENT DAYS
    const curbsideToday = curbsideAvailableDay === 'today';
    const curbsideTomorrow = curbsideAvailableDay === 'tomorrow';
    const inStoreToday = inStoreAvailableDay === 'today';
    const inStoreTomorrow = inStoreAvailableDay === 'tomorrow';
    if ((curbsideToday || curbsideTomorrow) && (inStoreToday || inStoreTomorrow)) {
      pickupLabel = (inStoreToday && pickupCutOffTimeFormatted)
        ? injectMessage(bopsMessages.inStoreTodayCurbsideTomorrow, { pickupCutOffTimeFormatted })
        : injectMessage(bopsMessages.inStoreTomorrowCurbsideToday, { pickupCutOffTimeFormatted });
    } else if (inStoreToday || inStoreTomorrow) {
      pickupLabel = (inStoreToday && pickupCutOffTimeFormatted)
        ? injectMessage(bopsMessages.inStoreTodayCurbsideFuture, { pickupCutOffTimeFormatted, otherDate })
        : injectMessage(bopsMessages.inStoreTomorrowCurbsideFuture, { otherDate });
    } else {
      pickupLabel = (curbsideToday && pickupCutOffTimeFormatted)
        ? injectMessage(bopsMessages.inStoreFutureCurbsideToday, { pickupCutOffTimeFormatted, otherDate })
        : injectMessage(bopsMessages.inStoreFutureCurbsideTomorrow, { otherDate });
    }
  } else if (pickupOptions && pickupOptions.length > 1) { // both available, inStore and curbside on the SAME DAYS
    pickupLabel = (deliveryDayLowerCase === 'today' && pickupCutOffTimeFormatted)
      ? injectMessage(bopsMessages.inStoreTodayCurbsideToday, { pickupCutOffTimeFormatted })
      : injectMessage(bopsMessages.inStoreTomorrowCurbsideTomorrow);
  } else if (pickupOptions && pickupOptions.length === 1) { // only one available between curbside and inStore
    if (pickupOptions[0] && (pickupOptions[0].toLowerCase() === 'in store')) {
      pickupLabel = (deliveryDayLowerCase === 'today' && pickupCutOffTimeFormatted)
        ? injectMessage(bopsMessages.inStoreTodayCurbsideUnavailable, { pickupCutOffTimeFormatted })
        : injectMessage(bopsMessages.inStoreTomorrowCurbsideUnavailable);
    } else {
      pickupLabel = (deliveryDayLowerCase === 'today' && pickupCutOffTimeFormatted)
        ? injectMessage(bopsMessages.inStoreUnavailableCurbsideToday, { pickupCutOffTimeFormatted })
        : injectMessage(bopsMessages.inStoreUnavailableCurbsideTomorrow);
    }
  }
  return pickupLabel;
};

// Generate the main Bops message based on store details
const generateBopsMessage = (storeDetails, isExperimentForPickupCopyClarity = false) => {
  const {
    deliveryDay, otherDate, pickupCutOffTime, pickupOptions, curbsideCutOffTime, cutOffTime,
  } = storeDetails;
  const deliveryDayLowerCase = deliveryDay?.toLowerCase?.();
  let displayDeliveryDay = deliveryDayLowerCase === 'today' || deliveryDayLowerCase === 'tomorrow' ? deliveryDayLowerCase : deliveryDay;
  const curbsideAvailableDay = curbsideCutOffTime?.pickupRelativeDay.toLowerCase?.();
  const inStoreAvailableDay = cutOffTime?.pickupRelativeDay.toLowerCase?.();
  let pickupLabel = '';

  if (isExperimentForPickupCopyClarity) {
    const pickupCutOffTimeFormatted = brand === 'mcom' ? formatTime(pickupCutOffTime || '4:00 PM')?.toLowerCase?.() : formatTime(pickupCutOffTime)?.toUpperCase?.();
    const { pickupMsg, pickupDeliveryDay } = generateExperimentPickupMessage(
      inStoreAvailableDay,
      curbsideAvailableDay,
      pickupCutOffTimeFormatted,
      displayDeliveryDay,
      pickupOptions,
    );
    pickupLabel = pickupMsg;
    displayDeliveryDay = pickupDeliveryDay || deliveryDayLowerCase;
  } else {
    const pickupCutOffTimeFormatted = formatTime(pickupCutOffTime);

    pickupLabel = generatePickupLabel(
      pickupOptions,
      curbsideAvailableDay,
      inStoreAvailableDay,
      pickupCutOffTimeFormatted,
      deliveryDayLowerCase,
      otherDate,
    );
  }

  const topLineMsg = injectMessage(bopsMessages.topLineMessage, {
    deliveryDay: displayDeliveryDay,
  });
  return {
    topLineMsg, pickupLabel, value: 'Pickup', disabled: false,
  };
};

export default generateBopsMessage;
